/**
 * What Wanderlight does, for somebody deciding whether to use it.
 *
 * Ported from `app/about/page.tsx`. **The copy is carried across verbatim** — every line
 * describes a feature that is already live, none of it is aspirational, and rewording it here
 * would silently make this page a second, drifting description of the product.
 *
 * Two things did change:
 *
 * - **The icons are MUI's, not lucide's.** Adding a second icon library to a project that
 *   already ships `@mui/icons-material` would be a dependency for no gain; each lucide glyph
 *   is mapped to its nearest Material equivalent below.
 * - **There is no auth gate**, matching the Next.js page's own reasoning: this is
 *   informational rather than a workspace, so there is nothing to protect. It is the one
 *   signed-in-app route a signed-out visitor can read, which is the point of it.
 */

import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import CameraAltOutlined from "@mui/icons-material/CameraAltOutlined";
import CropOutlined from "@mui/icons-material/CropOutlined";
import GppGoodOutlined from "@mui/icons-material/GppGoodOutlined";
import HowToRegOutlined from "@mui/icons-material/HowToRegOutlined";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import LayersOutlined from "@mui/icons-material/LayersOutlined";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import SettingsSuggestOutlined from "@mui/icons-material/SettingsSuggestOutlined";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import TroubleshootOutlined from "@mui/icons-material/TroubleshootOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import WbSunnyOutlined from "@mui/icons-material/WbSunnyOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

import { Section } from "@/components/Section";
import { BRAND_NAME, BRAND_TAGLINE } from "@/constants/brand";

interface Benefit {
  icon: SvgIconComponent;
  title: string;
  body: string;
}

/**
 * Grouped into four sections rather than one flat list, because the app has grown well past a
 * single screenful of bullet points. Each entry is written as a user-facing outcome — what
 * changes for them — rather than as a component or system name, since that is the audience.
 */
const CREATION_BENEFITS: readonly Benefit[] = [
  {
    icon: AutoAwesomeOutlined,
    title: "Two ways in",
    body: "Write a brief and create something new, or supply a photo you already have and describe how it should change. The original is always kept alongside the result.",
  },
  {
    icon: TuneOutlined,
    title: "You choose how much licence the AI has",
    body: "From a strict, camera-accurate correction of a real photo through to a fully symbolic, conceptual treatment — pick the level once, up front, rather than hoping the wording of a brief lands somewhere sensible.",
  },
  {
    icon: LayersOutlined,
    title: "Explore, refine, or perfect",
    body: "Generating more than one option means something specific now: Explore gives you genuinely different directions to choose between, Refine gives you close variations on the one you like, Perfect gives you a single considered take.",
  },
];

const DIRECTION_BENEFITS: readonly Benefit[] = [
  {
    icon: CameraAltOutlined,
    title: "Reads as photographed, not generated",
    body: "Every brief is built out into a real photographic setup: a plausible camera, lens and light for that specific scene, rather than a handful of AI buzzwords bolted on. It's built to hold up under a close look, not just a glance.",
  },
  {
    icon: WbSunnyOutlined,
    title: "Direct the shot, no jargon required",
    body: "Shape, portrait framing, time of day, weather: set what you care about with a few taps and leave the rest on Auto. Nothing requires knowing what an aperture is.",
  },
  {
    icon: CropOutlined,
    title: "Ready for social and web the moment it's made",
    body: "Pull a square post, a Story crop or a link-preview banner straight out of a result — click the image once to protect the part that matters, and every crop keeps it in frame instead of cutting it off.",
  },
  {
    icon: VisibilityOutlined,
    title: "Alt text written for you",
    body: "Every image gets a real, model-written description of what's actually in it, used automatically wherever the image appears — not left as a blank field someone has to remember to fill in later.",
  },
];

const CONSISTENCY_BENEFITS: readonly Benefit[] = [
  {
    icon: LayersOutlined,
    title: "One visual treatment across a whole project",
    body: "Start a series for an annual report or a campaign and every image after the first is generated to match it — same lighting, palette and lens character — instead of being recreated from scratch and hoping it looks related.",
  },
  {
    icon: SettingsSuggestOutlined,
    title: "Your organisation's own principles, set once",
    body: "Save a visual policy — approved environments, brand colours, house style, what to avoid — from Governance, and apply it to any generation in one click rather than re-explaining it in every brief.",
  },
];

const GOVERNANCE_BENEFITS: readonly Benefit[] = [
  {
    icon: GppGoodOutlined,
    title: "Guardrails for corporate use",
    body: "Briefs touching sustainability, ESG, real people or other authenticity-critical topics are flagged before anything is generated — and for the highest-risk combinations, a brief reason has to be recorded before proceeding, not just a checkbox ticked.",
  },
  {
    icon: HowToRegOutlined,
    title: "Rights and consent, recorded not assumed",
    body: "Editing a real photo asks who has the right to use it and whether everyone in it has consented; a fully synthetic image of people asks you to confirm they're not a real depiction of anyone. Both are part of the record, not an afterthought.",
  },
  {
    icon: TroubleshootOutlined,
    title: "A second, independent look for AI artefacts",
    body: "After generation, a separate automated check reviews the actual result for the kind of thing AI images are known to get wrong — malformed hands, garbled text — and flags it for a closer look before it goes anywhere.",
  },
  {
    icon: VerifiedOutlined,
    title: "A verifiable record for every image",
    body: "Every generation gets a downloadable integrity record — brief, controls, guardrail outcome, all in one document — and a signed content-credential manifest embedded in the file itself, readable by any C2PA-aware tool.",
  },
  {
    icon: ShareOutlined,
    title: "Where it's actually been published",
    body: "Mark a generation as published and where, and it shows up that way in the AI image register — a single exportable list a company secretary, legal or comms team can actually review.",
  },
  {
    icon: ScheduleOutlined,
    title: "Nothing gets lost, nothing overstays either",
    body: "Every image is saved to your history and downloadable at any time. Set your own retention window, from a day to forever, and delete anything yourself the moment you no longer need it.",
  },
  {
    icon: InsightsOutlined,
    title: "What it's actually costing, per client",
    body: "Real usage, not a flat guess — an admin-visible estimate computed from the model's own token counts, broken down per client alongside their monthly generation tier.",
  },
];

/**
 * A description list, not a grid of divs: each entry is genuinely a term and its definition,
 * and a screen reader announcing it as one is the difference between a list of features and a
 * run of unrelated headings.
 */
function BenefitGrid({ benefits }: { benefits: readonly Benefit[] }) {
  return (
    <Box component="dl" className="m-0 grid gap-6 sm:grid-cols-2">
      {benefits.map(({ icon: Icon, title, body }) => (
        <Stack key={title} spacing={0.75}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Icon aria-hidden="true" fontSize="small" sx={{ color: "primary.main" }} />
            <Typography component="dt" variant="subtitle2" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
          </Stack>
          <Typography component="dd" variant="body2" color="text.secondary" sx={{ m: 0 }}>
            {body}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

export function AboutPage() {
  return (
    <Stack spacing={5}>
      <Stack spacing={2}>
        <Typography variant="overline" className="text-white/80 text-on-video">
          {BRAND_NAME}
        </Typography>
        <Typography
          variant="h2"
          component="h1"
          className="text-white text-on-video"
          sx={{ letterSpacing: "-0.02em" }}
        >
          {BRAND_TAGLINE}
        </Typography>
        <Typography variant="body1" className="max-w-xl text-white/85 text-on-video">
          Create and enhance original imagery from the assets you already have.
        </Typography>
      </Stack>

      <Section
        title="Two ways to create"
        description="Every point below is a feature already built into Generate, not a roadmap."
      >
        <BenefitGrid benefits={CREATION_BENEFITS} />
      </Section>

      <Section title="Directed like a real shoot">
        <BenefitGrid benefits={DIRECTION_BENEFITS} />
      </Section>

      <Section
        title="Consistent across a whole project"
        description="For campaigns and reports, not just one-off images."
      >
        <BenefitGrid benefits={CONSISTENCY_BENEFITS} />
      </Section>

      <Section
        title="Governance you can actually show someone"
        description="Built for corporate use from the ground up, not bolted on afterwards."
      >
        <BenefitGrid benefits={GOVERNANCE_BENEFITS} />
      </Section>

      <Section title="Why Wanderlight">
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            The name Wanderlight suggests light released from the fixed conditions of
            photography: the physical camera, its position and lens; the weather and time of
            day; the place; and the single moment in which the shutter was pressed. A
            conventional photograph binds all these things together at once. Wanderlight
            loosens that bond.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            What remains constant is the physical realism of light: how it falls, how shadows
            and reflections behave, and how a real lens would see the scene. Everything around
            it is free to wander, the camera position, lens, location, weather and time of day;
            whether the image begins from a blank page or an existing photograph; and the
            variations explored before arriving at the final image.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Wanderlight names that separation: light remains true, while everything else is free
            to roam.
          </Typography>
        </Stack>
      </Section>

      <Box>
        <Button component={RouterLink} to="/generate" variant="contained" size="large">
          Create an image
        </Button>
      </Box>
    </Stack>
  );
}
